/*
 * lfy-trade-portal.js — PACKET-03A / D21. Renders GET /apps/cipzo/trade.
 *
 * ⛔ THE TWO ROUTES ANSWER A SIGNED-OUT VISITOR DIFFERENTLY, ON PURPOSE.
 *      GET  /apps/cipzo/trade        -> 200 { signedIn: false }   (renders INTO a page)
 *      POST /apps/cipzo/trade/lines  -> 401 { ok:false, reason }  (an API)
 *    So here a 200 is NOT success — it is the start of the question "which state is this?".
 *    `res.ok` is never consulted.
 *
 * ⛔ NO PRICE (D9). The read contract carries none. This file must never render, derive or cache
 *    one, and must never imply a trade price exists — tradePricing.ts is unwired on the Cipzo side.
 *
 * ⛔ REPORTED, NEVER INFERRED. `truncated` and `otherDraftsTruncated` are stated by the server,
 *    which fetches pageSize + 1 so "there is more" is OBSERVED rather than guessed from a
 *    full-looking page. Never re-derive either by measuring the array.
 */
(function () {
  "use strict";

  /* ⛔ RUN AFTER THE DOM EXISTS, WHATEVER ORDER THE SECTION EMITS US IN.
   * The section renders its script_tag BEFORE the markup, and `script_tag` produces a plain
   * parser-blocking <script src> — so this file executed while [data-lfy-portal] did not yet
   * exist, `root` was null, and it returned. The page then sat on "Loading your project…"
   * forever, which is the WORST failure shape: the initial state looks like working-but-slow,
   * so nothing reports and nobody can tell a hung fetch from a script that never ran.
   * Fixed here rather than by moving the tag, so re-ordering the section cannot reintroduce it. */
  function init() {
  var root = document.querySelector("[data-lfy-portal]");
  if (!root) return;

  function show(state) {
    var all = root.querySelectorAll("[data-state]");
    for (var i = 0; i < all.length; i++) all[i].hidden = all[i].getAttribute("data-state") !== state;
  }

  function text(el, value) {
    if (el) el.textContent = value;
  }

  function renderLines(lines) {
    var box = root.querySelector("[data-lines]");
    box.textContent = "";
    lines.forEach(function (l) {
      var row = document.createElement("div");
      row.className = "lfy-portal__line";
      row.setAttribute("role", "row");

      var main = document.createElement("div");
      main.className = "lfy-portal__lineMain";
      // title may legitimately be null — show the SKU rather than an empty row or the word "null".
      main.textContent = l.title || l.sku || "Product";
      if (l.sku && l.title) {
        var sku = document.createElement("span");
        sku.className = "lfy-portal__sku";
        sku.textContent = l.sku;
        main.appendChild(sku);
      }

      var qty = document.createElement("div");
      qty.className = "lfy-portal__qty";
      qty.textContent = "× " + l.quantity;

      row.appendChild(main);
      row.appendChild(qty);

      if (l.note) {
        var note = document.createElement("div");
        note.className = "lfy-portal__note";
        note.textContent = l.note;
        row.appendChild(note);
      }
      box.appendChild(row);
    });
  }

  function renderOthers(others, othersTruncated) {
    var wrap = root.querySelector("[data-others]");
    var list = root.querySelector("[data-otherlist]");
    list.textContent = "";
    if (!others || !others.length) {
      wrap.hidden = true;
      return;
    }
    others.forEach(function (d) {
      var li = document.createElement("li");
      var a = document.createElement("a");
      a.href = "#";
      a.textContent = d.name || "Untitled project";
      a.className = "lfy-portal__otherlink";
      li.appendChild(a);
      list.appendChild(li);
    });
    var t = root.querySelector("[data-others-truncated]");
    if (othersTruncated) {
      text(t, "This list is cut short — you have more open projects than are shown.");
      t.hidden = false;
    } else {
      t.hidden = true;
    }
    wrap.hidden = false;
  }

  /* The same failure SHAPE, closed properly rather than just this instance of it. A hung request
   * never rejects, so `.catch` never runs and the page would sit on "Loading your project…"
   * indefinitely — indistinguishable from working-but-slow, which is exactly why the script-order
   * bug went unreported. A loading state with no deadline is a silent failure waiting to happen. */
  var settled = false;
  var deadline = setTimeout(function () {
    if (!settled) show("unavailable");
  }, 15000);
  function settle(fn) {
    return function (arg) {
      settled = true;
      clearTimeout(deadline);
      return fn(arg);
    };
  }

  fetch(root.getAttribute("data-endpoint"), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  })
    .then(function (res) {
      // A gate denial 404s with NO body by design, so never assume one is parseable.
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          return { status: res.status, data: data || {} };
        });
    })
    .then(settle(function (r) {
      if (r.status === 404) return show("unavailable");
      if (r.status !== 200) return show("unavailable");

      var d = r.data;

      // Signed-out is a legitimate 200 on this route, not a failure.
      if (!d.signedIn) return show("signed-out");

      if (!d.trade || !d.trade.approved) {
        var why = root.querySelector("[data-not-approved-reason]");
        if (d.trade && d.trade.reason) {
          text(why, "Status: " + d.trade.reason);
          why.hidden = false;
        }
        return show("not-approved");
      }

      if (!d.project) return show("empty");

      text(root.querySelector("[data-project-name]"), d.project.name || "Untitled project");
      var meta = [];
      if (d.project.reference) meta.push(d.project.reference);
      if (d.project.status) meta.push(d.project.status);
      if (d.trade.companyName) meta.push(d.trade.companyName);
      text(root.querySelector("[data-project-meta]"), meta.join(" · "));

      renderLines(Array.isArray(d.lines) ? d.lines : []);

      var trunc = root.querySelector("[data-truncated]");
      if (d.truncated) {
        // Stated, not measured. Saying "showing N of N" from the array length would be the exact
        // lie this flag exists to prevent.
        text(trunc, "This project has more lines than are shown here.");
        trunc.hidden = false;
      } else {
        trunc.hidden = true;
      }

      renderOthers(d.otherDrafts, d.otherDraftsTruncated);
      show("project");
    }))
    .catch(settle(function () {
      // Transport failure is not a refusal, and must not read as "you have no project".
      show("unavailable");
    }));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
